import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, TextField, Button, MenuItem, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, Stack,
    IconButton, Tooltip, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import api from '../api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'AGENT' });
    const [editingUserId, setEditingUserId] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/users/');
            setUsers(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch users", err);
        }
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        try {
            if (editingUserId) {
                const dataToSave = { ...newUser };
                // Si la contraseña está vacía durante la edición, no la enviamos para no sobreescribirla
                if (!dataToSave.password) delete dataToSave.password;
                
                await api.patch(`/users/${editingUserId}/`, dataToSave);
                setMessage({ text: 'Usuario actualizado correctamente', type: 'success' });
            } else {
                await api.post('/users/', newUser);
                setMessage({ text: 'Usuario creado correctamente', type: 'success' });
            }
            setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'AGENT' });
            setEditingUserId(null);
            fetchUsers();
        } catch (err) {
            setMessage({ text: 'Error al procesar el usuario. Verifique los datos.', type: 'error' });
        }
    };

    const handleEdit = (user) => {
        setEditingUserId(user.id);
        setNewUser({
            email: user.email,
            password: '', // No mostramos la contraseña actual por seguridad
            first_name: user.first_name,
            last_name: user.last_name,
            role: user.role
        });
    };

    const handleToggleStatus = async (user) => {
        const action = user.is_active ? 'desactivar' : 'activar';
        if (window.confirm(`¿Estás seguro de que deseas ${action} a ${user.full_name}?`)) {
            try {
                await api.patch(`/users/${user.id}/`, { is_active: !user.is_active });
                setMessage({ text: `Usuario ${action}ado correctamente`, type: 'success' });
                fetchUsers();
            } catch (err) {
                setMessage({ text: 'Error al cambiar el estado del usuario', type: 'error' });
            }
        }
    };

    const handleDeleteUser = async (user) => {
        if (window.confirm(`¡ADVERTENCIA CRÍTICA! Eliminar a ${user.full_name} borrará permanentemente todos los tickets que este usuario haya creado. ¿Estás absolutamente seguro?`)) {
            try {
                await api.delete(`/users/${user.id}/`);
                setMessage({ text: 'Usuario eliminado permanentemente', type: 'success' });
                fetchUsers();
            } catch (err) {
                setMessage({ text: 'Error al eliminar el usuario', type: 'error' });
            }
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">Gestión de Usuarios Internos</Typography>
            </Box>
            
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>{editingUserId ? 'Editar Usuario' : 'Agregar Nuevo Agente/Admin'}</Typography>
                {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({text: '', type: ''})}>{message.text}</Alert>}
                <form onSubmit={handleSaveUser}>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <TextField label="Nombre" fullWidth value={newUser.first_name} onChange={e => setNewUser({...newUser, first_name: e.target.value})} required />
                        <TextField label="Apellido" fullWidth value={newUser.last_name} onChange={e => setNewUser({...newUser, last_name: e.target.value})} required />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <TextField label="Correo Electrónico" type="email" fullWidth value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required />
                        <TextField 
                            label="Contraseña" 
                            type="password" 
                            fullWidth 
                            value={newUser.password} 
                            onChange={e => setNewUser({...newUser, password: e.target.value})} 
                            required={!editingUserId} 
                            helperText={editingUserId ? "Deje en blanco para mantener la actual" : ""}
                        />
                        <TextField select label="Rol" fullWidth value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                            <MenuItem value="AGENT">Agente de Soporte</MenuItem>
                            <MenuItem value="ADMIN">Administrador</MenuItem>
                        </TextField>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button type="submit" variant="contained">
                            {editingUserId ? 'Actualizar Usuario' : 'Crear Usuario'}
                        </Button>
                        {editingUserId && (
                            <Button variant="outlined" onClick={() => {
                                setEditingUserId(null);
                                setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'AGENT' });
                            }}>
                                Cancelar
                            </Button>
                        )}
                    </Stack>
                </form>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell>Nombre Completo</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Rol</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Idioma</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.full_name}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={user.is_active ? "Activo" : "Inactivo"} 
                                        color={user.is_active ? "success" : "default"} 
                                        size="small" 
                                    />
                                </TableCell>
                                <TableCell>{user.preferred_language}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Editar">
                                        <IconButton onClick={() => handleEdit(user)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={user.is_active ? "Desactivar" : "Activar"}>
                                        <IconButton onClick={() => handleToggleStatus(user)} color={user.is_active ? "warning" : "success"}>
                                            {user.is_active ? <PersonOffIcon /> : <PersonIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Permanentemente">
                                        <IconButton onClick={() => handleDeleteUser(user)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default UserManagement;